import { useStudents } from '@/app/redux/hooks/students';
import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import { Assessment } from '@/common/types/assessment';
import { ControllerType } from '@/common/types/student';
import { useIsFeatureEnabled } from '@/common/ui/FeatureGate';
import InitialsCircle from '@/common/ui/InitialsCircle';
import {
  DotsThreeVerticalIcon,
  MouseLeftClickIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import { Checkbox } from '@shadcn/components/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shadcn/components/dropdown-menu';
import { Label } from '@shadcn/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shadcn/components/select';
import { TableCell, TableRow } from '@shadcn/components/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { cn } from '@shadcn/lib/utils';
import dayjs from 'dayjs';
import { Keyboard } from 'lucide-react';
import { useMemo, useState } from 'react';

interface AssessmentsTableRowProps {
  assessment: Assessment;
  visibleStudentIds?: string[];
  onSelect: () => void;
  handlePhaseChange: (phase: number) => void;
  handleControlTypeChange: (control: ControllerType) => void;
  handlePlacementChange: (placeAtAssessedLevel: boolean) => void;
  handleDeleteAssessment: () => void;
}

export default function AssessmentsTableRow({
  assessment,
  visibleStudentIds,
  onSelect,
  handlePhaseChange,
  handleControlTypeChange,
  handlePlacementChange,
  handleDeleteAssessment,
}: AssessmentsTableRowProps) {
  const isFeatureEnabled = useIsFeatureEnabled();

  const { studentsById } = useStudents();

  const [isDropdownOpen, setDropdownOpen] = useState(false);

  const { notStarted, started, completed } = useMemo(() => {
    return {
      notStarted: Object.entries(assessment.students)
        .filter(
          ([id, s]) => !!studentsById.get(id) && !s.startedAt && !s.completedAt,
        )
        .map(([id]) => id),
      started: Object.entries(assessment.students)
        .filter(([, s]) => !!s.startedAt && !s.completedAt)
        .map(([id]) => id),
      completed: Object.entries(assessment.students)
        .filter(([, s]) => !!s.completedAt)
        .map(([id]) => id),
    };
  }, [assessment.students, studentsById]);

  const editingDisabled = useMemo(
    () =>
      !isFeatureEnabled('assessments') ||
      notStarted.length !== Object.keys(assessment.students).length,
    [notStarted, assessment, isFeatureEnabled],
  );

  function getStudentName(studentId: string) {
    const student = studentsById.get(studentId);
    if (student) {
      return `${student.studentFirstName} ${student.studentSurname}`;
    } else {
      return `Unknown Learner`;
    }
  }

  const renderStudentsCell = (studentIds: string[]) => {
    const visibleSet = new Set(visibleStudentIds ?? studentIds);
    const visibleStudents = studentIds.filter((id) => visibleSet.has(id));
    const invisibleStudents = studentIds.filter((id) => !visibleSet.has(id));

    const displayedStudents = visibleStudents.slice(0, 4);
    const remainingCount = studentIds.length - displayedStudents.length;
    const hasMoreStudents = remainingCount > 0;

    const getRemainingStudentNames = () => {
      const hiddenVisible = visibleStudents.slice(4).map(getStudentName);
      const hiddenInvisible = invisibleStudents.map(getStudentName);
      return [...hiddenVisible, ...hiddenInvisible].join(', ');
    };

    return (
      <TableCell className="px-4 w-[120px]">
        <div className="pl-2 flex items-center">
          {displayedStudents.map((studentId) => (
            <Tooltip key={studentId}>
              <TooltipTrigger className="-ml-1">
                <InitialsCircle
                  userName={getStudentName(studentId)}
                  size={24}
                />
              </TooltipTrigger>
              <TooltipContent>{getStudentName(studentId)}</TooltipContent>
            </Tooltip>
          ))}

          {hasMoreStudents && (
            <Tooltip>
              <TooltipTrigger className="-ml-1 h-6 w-6 border border-border flex items-center justify-center bg-white rounded-full relative z-10 font-medium text-xs text-muted-foreground">
                +{remainingCount}
              </TooltipTrigger>
              <TooltipContent>{getRemainingStudentNames()}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </TableCell>
    );
  };

  return (
    <TableRow className="cursor-pointer" onClick={onSelect}>
      <TableCell className="pl-4 text-sm w-[180px]">
        {dayjs(assessment.createdAt).format('DD/MM/YY h:mm a')}
      </TableCell>
      {renderStudentsCell(notStarted)}
      {renderStudentsCell(started)}
      {renderStudentsCell(completed)}
      <TableCell className="px-4">
        <Select
          value={assessment.phase.toString()}
          onValueChange={(value) => handlePhaseChange(Number(value))}
          disabled={editingDisabled}
        >
          <SelectTrigger id="phase" className="min-w-[150px]">
            {assessment.phase === 0 ? (
              <span className="font-medium">Screener (Phases 1-3)</span>
            ) : (
              <span className="font-medium">Phase {assessment.phase}</span>
            )}
          </SelectTrigger>
          <SelectContent className="w-[var(--radix-select-trigger-width)]">
            <SelectItem value={'0'} className="max-w-md">
              <span className="font-medium">Screener (Phases 1-3)</span>
              <br />
              <span className="text-xs text-muted-foreground">
                Start with a screener to assess all of Phases 1-3 at once.
              </span>
            </SelectItem>
            {scopeAndSequence.phases.map((phase) => (
              <SelectItem key={phase.phase} value={phase.phase.toString()}>
                <span className="font-medium">Phase {phase.phase}</span>
                <br />
                <span className="text-xs text-muted-foreground">
                  {phase.description}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="px-4">
        <Select
          value={assessment.control}
          onValueChange={(value) =>
            handleControlTypeChange(value as ControllerType)
          }
          disabled={editingDisabled}
        >
          <SelectTrigger id="control">
            <SelectValue placeholder="Select control type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ControllerType.KEYBOARD}>
              <span className="flex items-center gap-2">
                <Keyboard size={16} />
                Typing
              </span>
            </SelectItem>
            <SelectItem value={ControllerType.CLICK}>
              <span className="flex items-center gap-2">
                <MouseLeftClickIcon size={16} />
                Click to Place
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="px-4">
        <Label
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'flex items-center gap-2 text-sm w-fit',
            editingDisabled && 'text-muted-foreground',
          )}
        >
          <Checkbox
            checked={assessment.placeAtAssessedLevel}
            onCheckedChange={(checked) =>
              handlePlacementChange(checked as boolean)
            }
            disabled={editingDisabled}
          />
          <span>Place automatically</span>
        </Label>
      </TableCell>
      <TableCell>
        <div className="ml-4">
          <DropdownMenu open={isDropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="p-1/2 mt-1"
                onClick={(e) => e.stopPropagation()}
              >
                <DotsThreeVerticalIcon size={24} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenuItem
                className="flex gap-2"
                onClick={() => {
                  setDropdownOpen(false);
                  handleDeleteAssessment();
                }}
              >
                <TrashIcon weight="bold" size={16} color="red" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
}
